import {
    Text,
    Link,
    Card,
    CardHeader,
    Divider,
    makeStyles,
    mergeClasses,
    tokens,
    Avatar,
} from '@fluentui/react-components';
import { AgentOwnerInfo } from '../../../../domain/agents/models/Agent';
import useOverviewStyles from '../styles/AgentOverviewStyles';
import { customColors, customFonts } from '../../../../styles';

const useStyles = makeStyles({
    cardOverrides: {
        alignItems: 'center',
        paddingBlock: '25px',
        paddingInline: '25px',
        textAlign: 'center',
        gap: '30px',
    },

    cardHeaderOverrides: {
        alignItems: 'center',
        width: 'fit-content',
        alignSelf: 'center',
        textAlign: 'center',
    },

    ownerSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
    },
    ownerInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
    },
    ownerImage: {
        width: '128px',
        height: '128px',
        borderRadius: '15px',
    },
    ownerName: {
        fontFamily: customFonts.TitleFont,
        color: customColors.protivitiDarkBlue,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase500,
        textAlign: 'center',
    },
    ownerEmail: {
        color: customColors.linkColor,
        textDecoration: 'underline',
        fontSize: tokens.fontSizeBase300,
        textAlign: 'center',
    },
    ownersBlurb: {
        marginBlockStart: '12px',
        color: customColors.protivitiDarkBlue,
        fontFamily: customFonts.Lato,
        fontStyle: 'italic',
        fontSize: '1.1rem',
        lineHeight: 1.25,
        textAlign: 'center',
    },

    coOwnerSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    coOwners: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },

    ownerNameAndEmail: {
        display: 'flex',
        flexDirection: 'column',
    },
});

interface AgentOwnersCardProps {
    ownersInfo: AgentOwnerInfo[] | undefined;
    ownersBlurb: string | undefined;
}

const AgentOwnersCard: React.FC<AgentOwnersCardProps> = ({ ownersInfo, ownersBlurb }) => {

    const styles = useStyles();
    const overviewStyles = useOverviewStyles();

    const firstOwner = ownersInfo && ownersInfo.length > 0 ? ownersInfo[0] : undefined;
    const coOwners = ownersInfo && ownersInfo.length > 1 ? ownersInfo.slice(1) : [];

    return (
        <Card className={mergeClasses(overviewStyles.card, styles.cardOverrides)}>
            <section className={styles.ownerSection}>
                <CardHeader
                    className={mergeClasses(overviewStyles.cardHeader, styles.cardHeaderOverrides)}
                    header={
                        <header className={mergeClasses(overviewStyles.header, styles.cardHeaderOverrides)}>
                            <Text className={overviewStyles.headerTitle}>Agent Owners</Text>
                            <Divider />
                        </header>
                    }
                />

                <div className={styles.ownerInfo}>
                    <Avatar
                        image={{ src: firstOwner?.photoUrl }}
                        shape="square"
                        size={128}
                        className={styles.ownerImage}
                    />
                    <OwnerNameAndEmail key={firstOwner?.userPrincipalName} owner={firstOwner} />

                    {ownersBlurb && ownersBlurb.trim() !== '' && (
                        <Text className={styles.ownersBlurb}>"{ownersBlurb}"</Text>
                    )}
                </div>
            </section>

            {coOwners && coOwners.length > 0 && (
                <section className={styles.coOwnerSection}>
                    <CardHeader
                        className={mergeClasses(overviewStyles.cardHeader, styles.cardHeaderOverrides)}
                        header={
                            <header className={mergeClasses(overviewStyles.header, styles.cardHeaderOverrides)}>
                                <Text className={overviewStyles.headerTitle}>Co-Owners</Text>
                                <Divider />
                            </header>
                        }
                    />

                    <div className={styles.coOwners}>
                        {coOwners.map((coOwner) => (
                            <OwnerNameAndEmail key={coOwner.userPrincipalName} owner={coOwner} />
                        ))}
                    </div>
                </section>
            )}
        </Card>
    );
};

interface OwnerDisplayProps {
    owner: AgentOwnerInfo | undefined;
}

const OwnerNameAndEmail: React.FC<OwnerDisplayProps> = ({ owner }) => {
    const styles = useStyles();
    return (
        <div className={styles.ownerNameAndEmail}>
            <Text className={styles.ownerName}>
                {owner?.givenName && owner?.surName
                    ? `${owner.givenName} ${owner.surName}`
                    : 'No owner information available'}
            </Text>
            <Link
                className={styles.ownerEmail}
                href={`mailto:${owner?.userPrincipalName ?? ''}?subject=GPT%20Feedback`}
                target="_blank"
                rel="noreferrer"
            >
                {owner?.userPrincipalName ?? ''}
            </Link>
        </div>
    );
};

export default AgentOwnersCard;
